import {
  createContext,
  useCallback,
  useContext,
  useEffect,
  useState,
} from 'react';
import type { User } from '@asset-mgmt/shared';
import { authApi } from '../lib/api';

// ── Types ──────────────────────────────────────────────────────────────────────

interface AuthState {
  user: User | null;
  googleLinked: boolean;
  hasPassword: boolean;
  loading: boolean;
  error: string | null;
}

interface AuthActions {
  login: (email: string, password: string) => Promise<void>;
  register: (email: string, password: string, displayName: string) => Promise<void>;
  logout: () => Promise<void>;
  /** Redirects the browser to the Google OAuth flow */
  loginWithGoogle: () => void;
  clearError: () => void;
  updateSettings: (settings: Partial<User['settings']>) => Promise<void>;
  updateProfile: (profile: { displayName: string }) => Promise<void>;
  refreshUser: () => Promise<void>;
}

type AuthContextValue = AuthState & AuthActions;

// ── Context ────────────────────────────────────────────────────────────────────

const AuthContext = createContext<AuthContextValue | null>(null);

// ── Provider ───────────────────────────────────────────────────────────────────

export function AuthProvider({ children }: { children: React.ReactNode }) {
  const [state, setState] = useState<AuthState>({
    user: null,
    googleLinked: false,
    hasPassword: false,
    loading: true,
    error: null,
  });

  // Check existing session on mount
  useEffect(() => {
    authApi
      .me()
      .then(({ user, googleLinked, hasPassword }) => setState({ user, googleLinked, hasPassword, loading: false, error: null }))
      .catch(() => setState({ user: null, googleLinked: false, hasPassword: false, loading: false, error: null }));
  }, []);

  const login = useCallback(async (email: string, password: string) => {
    setState((s) => ({ ...s, loading: true, error: null }));
    try {
      const { user } = await authApi.login({ email, password });
      setState({ user, googleLinked: false, hasPassword: true, loading: false, error: null });
    } catch (e) {
      setState((s) => ({
        ...s,
        loading: false,
        error: e instanceof Error ? e.message : 'Login failed',
      }));
      throw e;
    }
  }, []);

  const register = useCallback(
    async (email: string, password: string, displayName: string) => {
      setState((s) => ({ ...s, loading: true, error: null }));
      try {
        const { user } = await authApi.register({ email, password, displayName });
        setState({ user, googleLinked: false, hasPassword: true, loading: false, error: null });
      } catch (e) {
        setState((s) => ({
          ...s,
          loading: false,
          error: e instanceof Error ? e.message : 'Registration failed',
        }));
        throw e;
      }
    },
    [],
  );

  const logout = useCallback(async () => {
    await authApi.logout().catch(() => {});
    setState({ user: null, googleLinked: false, hasPassword: false, loading: false, error: null });
  }, []);

  const loginWithGoogle = useCallback(() => {
    window.location.href = '/api/auth/google';
  }, []);

  const clearError = useCallback(() => {
    setState((s) => ({ ...s, error: null }));
  }, []);

  const updateSettings = useCallback(async (settings: Partial<User['settings']>) => {
    const { user } = await authApi.updateSettings(settings);
    setState((s) => ({ ...s, user }));
  }, []);

  const updateProfile = useCallback(async (profile: { displayName: string }) => {
    const { user } = await authApi.updateProfile(profile);
    setState((s) => ({ ...s, user }));
  }, []);

  const refreshUser = useCallback(async () => {
    const { user, googleLinked, hasPassword } = await authApi.me();
    setState((s) => ({ ...s, user, googleLinked, hasPassword }));
  }, []);

  return (
    <AuthContext.Provider
      value={{ ...state, login, register, logout, loginWithGoogle, clearError, updateSettings, updateProfile, refreshUser }}
    >
      {children}
    </AuthContext.Provider>
  );
}

// ── Hook ───────────────────────────────────────────────────────────────────────

export function useAuth(): AuthContextValue {
  const ctx = useContext(AuthContext);
  if (!ctx) throw new Error('useAuth must be used inside <AuthProvider>');
  return ctx;
}
